import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Calendar,
  Trophy,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import Header from './Header';
import Footer from './Footer';

const ATHLETE_NAV = [
  { to: '/athlete/dashboard', icon: LayoutDashboard, label: '首頁', labelEn: 'Dashboard' },
  { to: '/athlete/profile', icon: User, label: '個人資料', labelEn: 'Profile' },
  { to: '/athlete/event', icon: Calendar, label: '賽事資訊', labelEn: 'My Event' },
  { to: '/athlete/competitions', icon: Trophy, label: '競賽項目', labelEn: 'My Competitions' },
] as const;

export default function AthleteLayout() {
  const { athlete, logout } = useAthleteAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Athlete sub-nav bar */}
      <div className="bg-[var(--bg-card)] border-b border-[var(--border)] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-12">
          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {ATHLETE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[var(--primary)] text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
                <span className="text-xs opacity-60 hidden lg:inline">/ {item.labelEn}</span>
              </NavLink>
            ))}
          </nav>

          {/* Athlete info + logout */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-xs font-bold">
                {athlete?.name?.charAt(0) || '?'}
              </div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {athlete?.name}
              </span>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>登出</span>
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-1.5 rounded-md hover:bg-[var(--bg-secondary)]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle athlete menu"
          >
            {sidebarOpen ? (
              <X className="w-5 h-5 text-[var(--text-primary)]" />
            ) : (
              <Menu className="w-5 h-5 text-[var(--text-primary)]" />
            )}
          </button>

          {/* Mobile: athlete name */}
          <div className="md:hidden flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-xs font-bold">
              {athlete?.name?.charAt(0) || '?'}
            </div>
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {athlete?.name}
            </span>
          </div>
        </div>

        {/* Mobile dropdown */}
        {sidebarOpen && (
          <div className="md:hidden border-t border-[var(--border)] px-4 py-2 space-y-1">
            {ATHLETE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[var(--primary)] text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
                <span className="text-xs opacity-60">/ {item.labelEn}</span>
              </NavLink>
            ))}
            <button
              onClick={() => {
                setSidebarOpen(false);
                logout();
              }}
              className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>登出 / Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="flex-1 bg-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Outlet />
        </div>
      </main>

      <Footer />
    </div>
  );
}
